import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAdminOverview } from '../api/endpoints';
import { PageHeader } from '../components/AppShell';
import { ErrorNotice, LoadingRows, SectionTitle } from '../components/Feedback';
import { Mono } from '../components/Pills';
import { formatRelative, formatTimestamp } from '../lib/format';
import { useAuth, useResource } from '../state';

export function AdminDashboardPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const overviewResource = useResource(() => getAdminOverview(), [], {
    enabled: isAdmin,
    live: ['run.finished'],
  });

  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState<'all' | 'paid' | 'admin'>('all');

  const data = overviewResource.data;

  // Filtered users list
  const filteredUsers = useMemo(() => {
    if (!data?.users) return [];
    return data.users.filter((u) => {
      const matchSearch =
        u.email.toLowerCase().includes(search.toLowerCase()) ||
        u.organizationName.toLowerCase().includes(search.toLowerCase()) ||
        u.plan.toLowerCase().includes(search.toLowerCase());

      if (!matchSearch) return false;

      if (filterPlan === 'paid') return u.plan !== 'free';
      if (filterPlan === 'admin') return u.role === 'admin';
      return true;
    });
  }, [data?.users, search, filterPlan]);

  if (!isAdmin) {
    return (
      <div className="page">
        <PageHeader
          title="Owner Master Dashboard"
          subtitle="Restricted to instance operators and founders."
        />
        <div className="notice notice--error" role="alert">
          <div>
            <p className="notice__title">Access Restricted</p>
            <p className="notice__message">
              This master dashboard is reserved for the platform <strong>owner / super-admin</strong>. Your account
              does not have administrative privileges to view global multi-tenant metrics.
            </p>
            <p className="muted" style={{ marginTop: '8px', fontSize: '13px' }}>
              Signed in as <strong>{user?.email ?? 'Unknown'}</strong> (role: <code>{user?.role ?? 'member'}</code>)
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="Owner Master Dashboard"
        subtitle="Platform-wide multi-tenant intelligence: users, revenue, AI generation distribution, and credit liquidity."
        actions={
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Link to="/admin/llm" className="btn btn--small btn--secondary">
              Configure Hosted LLM ↗
            </Link>
            <button
              type="button"
              className="btn btn--small"
              onClick={() => void overviewResource.reload()}
              disabled={overviewResource.loading}
            >
              {overviewResource.loading ? 'Refreshing...' : 'Refresh Metrics'}
            </button>
          </div>
        }
      />

      {overviewResource.error ? (
        <ErrorNotice error={overviewResource.error} onRetry={overviewResource.reload} />
      ) : null}

      {overviewResource.loading && !data ? (
        <LoadingRows rows={6} label="Loading platform intelligence" />
      ) : null}

      {data ? (
        <>
          {/* ------------------------------------------------ Top KPI Metric Cards */}
          <section className="admin-kpi-grid">
            {/* Total Users */}
            <div className="admin-kpi-card">
              <div className="admin-kpi-card__label">Total Registered Users</div>
              <div className="admin-kpi-card__value">{data.kpis.totalUsers}</div>
              <div className="admin-kpi-card__sub">
                Across <strong>{data.kpis.totalOrganizations}</strong> tenant workspaces
              </div>
            </div>

            {/* Estimated Revenue / Monetization */}
            <div className="admin-kpi-card admin-kpi-card--highlight">
              <div className="admin-kpi-card__label">Estimated Platform Revenue</div>
              <div className="admin-kpi-card__value">
                ${data.revenue.estimatedRevenueUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div className="admin-kpi-card__sub">
                {data.revenue.totalCreditPurchased.toLocaleString()} credits bought ($0.0285 / cr)
              </div>
            </div>

            {/* Test Runs & Pass Rate */}
            <div className="admin-kpi-card">
              <div className="admin-kpi-card__label">Total Runs &amp; Pass Rate</div>
              <div className="admin-kpi-card__value">
                {data.kpis.totalRuns}{' '}
                <span style={{ fontSize: '1rem', color: 'var(--pass)' }}>
                  ({data.kpis.platformPassRate}% pass)
                </span>
              </div>
              <div className="admin-kpi-card__sub">
                Across <strong>{data.kpis.totalProjects}</strong> projects &amp; {data.kpis.totalTests} tests
              </div>
            </div>

            {/* AI Test Generations */}
            <div className="admin-kpi-card">
              <div className="admin-kpi-card__label">AI Test Generations</div>
              <div className="admin-kpi-card__value">{data.aiUsage.totalGenerations}</div>
              <div className="admin-kpi-card__sub">
                <strong>{data.aiUsage.hostedGenerations}</strong> hosted ({data.aiUsage.creditsSpentOnAi} cr) ·{' '}
                <strong>{data.aiUsage.byokGenerations}</strong> BYOK
              </div>
            </div>
          </section>

          {/* ------------------------------------------- AI Generation Distribution */}
          <section className="panel" style={{ marginTop: '24px' }}>
            <SectionTitle
              actions={
                <Link to="/admin/llm" className="btn btn--small btn--secondary">
                  Manage Provider Keys →
                </Link>
              }
            >
              AI Test Generation Distribution &amp; Monetization
            </SectionTitle>

            <div className="admin-ai-grid">
              <div className="admin-ai-meter-card">
                <h4>Hosted Operator Model vs BYOK (Bring Your Own Key)</h4>
                <p className="muted" style={{ fontSize: '0.85rem', marginBottom: '16px' }}>
                  When customers use the <strong>Hosted Model</strong>, Justloop bills them 2 credits (~$0.057) per draft.
                  When customers use <strong>BYOK</strong>, they supply their own API key and Justloop charges 0 credits.
                </p>

                {/* Progress Distribution Bar */}
                <div className="admin-split-bar" role="img" aria-label="Hosted vs BYOK split">
                  <div
                    className="admin-split-bar__fill admin-split-bar__fill--hosted"
                    style={{
                      width: `${
                        data.aiUsage.totalGenerations > 0
                          ? (data.aiUsage.hostedGenerations / data.aiUsage.totalGenerations) * 100
                          : 50
                      }%`,
                    }}
                    title={`Hosted: ${data.aiUsage.hostedGenerations}`}
                  />
                  <div
                    className="admin-split-bar__fill admin-split-bar__fill--byok"
                    style={{
                      width: `${
                        data.aiUsage.totalGenerations > 0
                          ? (data.aiUsage.byokGenerations / data.aiUsage.totalGenerations) * 100
                          : 50
                      }%`,
                    }}
                    title={`BYOK: ${data.aiUsage.byokGenerations}`}
                  />
                </div>

                <div className="admin-split-legend">
                  <div className="admin-split-legend__item">
                    <span className="admin-split-dot admin-split-dot--hosted" />
                    <span>
                      <strong>Hosted Model:</strong> {data.aiUsage.hostedGenerations} calls (
                      {data.aiUsage.totalGenerations > 0
                        ? Math.round((data.aiUsage.hostedGenerations / data.aiUsage.totalGenerations) * 100)
                        : 0}
                      %)
                    </span>
                  </div>
                  <div className="admin-split-legend__item">
                    <span className="admin-split-dot admin-split-dot--byok" />
                    <span>
                      <strong>BYOK:</strong> {data.aiUsage.byokGenerations} calls (
                      {data.aiUsage.totalGenerations > 0
                        ? Math.round((data.aiUsage.byokGenerations / data.aiUsage.totalGenerations) * 100)
                        : 0}
                      %)
                    </span>
                  </div>
                </div>
              </div>

              {/* Provider Status */}
              <div className="admin-ai-provider-card">
                <h4>Hosted Provider Configuration</h4>
                {data.aiUsage.provider?.configured ? (
                  <div className="admin-provider-details">
                    <div className="admin-detail-row">
                      <span className="muted">Provider:</span>
                      <strong style={{ textTransform: 'capitalize' }}>
                        {data.aiUsage.provider.id ?? 'Unknown'}
                      </strong>
                    </div>
                    <div className="admin-detail-row">
                      <span className="muted">Model:</span>
                      <Mono>{data.aiUsage.provider.model ?? 'default'}</Mono>
                    </div>
                    <div className="admin-detail-row">
                      <span className="muted">Key Status:</span>
                      <span className="pill pill--pass">Active (Server Secret)</span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="muted">State:</span>
                      {data.aiUsage.provider.enabled !== false ? (
                        <span className="pill pill--running">Enabled for Users</span>
                      ) : (
                        <span className="pill pill--warn">Disabled</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="admin-provider-empty">
                    <p className="muted" style={{ margin: 0, fontSize: '0.9rem' }}>
                      No hosted provider configured yet. Callers currently must supply their own API key (BYOK).
                    </p>
                    <Link to="/admin/llm" className="btn btn--small" style={{ marginTop: '12px' }}>
                      Configure Provider Key Now →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* --------------------------------------------- Registered Users Table */}
          <section className="panel" style={{ marginTop: '24px' }}>
            <SectionTitle
              actions={
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="search"
                    className="field__input"
                    style={{ width: '220px', padding: '6px 10px', fontSize: '0.85rem' }}
                    placeholder="Search user or org..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <div className="admin-filter-tabs">
                    <button
                      type="button"
                      className={`btn btn--small ${filterPlan === 'all' ? '' : 'btn--secondary'}`}
                      onClick={() => setFilterPlan('all')}
                    >
                      All ({data.users.length})
                    </button>
                    <button
                      type="button"
                      className={`btn btn--small ${filterPlan === 'paid' ? '' : 'btn--secondary'}`}
                      onClick={() => setFilterPlan('paid')}
                    >
                      Paid Plans
                    </button>
                    <button
                      type="button"
                      className={`btn btn--small ${filterPlan === 'admin' ? '' : 'btn--secondary'}`}
                      onClick={() => setFilterPlan('admin')}
                    >
                      Admins
                    </button>
                  </div>
                </div>
              }
            >
              Registered Users &amp; Tenant Workspaces ({filteredUsers.length})
            </SectionTitle>

            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>User Email</th>
                    <th>Role</th>
                    <th>Organization</th>
                    <th>Plan</th>
                    <th className="num">Credits Remaining</th>
                    <th className="num">Projects</th>
                    <th className="num">Tests</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: '24px' }}>
                        No users match the search filter.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <strong>{u.email}</strong>
                        </td>
                        <td>
                          {u.role === 'admin' ? (
                            <span className="pill pill--pass" style={{ fontSize: '11px', fontWeight: 700 }}>
                              SUPER ADMIN
                            </span>
                          ) : (
                            <span className="pill pill--pending" style={{ fontSize: '11px' }}>
                              member
                            </span>
                          )}
                        </td>
                        <td>{u.organizationName}</td>
                        <td>
                          <span
                            className={`pill ${
                              u.plan === 'free'
                                ? 'pill--pending'
                                : u.plan === 'starter'
                                ? 'pill--running'
                                : 'pill--pass'
                            }`}
                            style={{ textTransform: 'capitalize' }}
                          >
                            {u.plan}
                          </span>
                        </td>
                        <td className="num" style={{ fontWeight: 600 }}>
                          {u.creditsRemaining}
                        </td>
                        <td className="num">{u.projectsCount}</td>
                        <td className="num">{u.testsCount}</td>
                        <td title={formatTimestamp(u.createdAt)}>{formatRelative(u.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* --------------------------------- Global Platform Transactions Ledger */}
          <section className="panel" style={{ marginTop: '24px' }}>
            <SectionTitle>Global Credit &amp; Revenue Transactions (Audit Feed)</SectionTitle>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Organization</th>
                    <th>Reason / Details</th>
                    <th className="num">Delta Credits</th>
                    <th>Transaction ID</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: '20px' }}>
                        No platform credit transactions recorded yet.
                      </td>
                    </tr>
                  ) : (
                    data.recentTransactions.map((tx) => (
                      <tr key={tx.id}>
                        <td title={formatTimestamp(tx.at)}>{formatRelative(tx.at)}</td>
                        <td>
                          <strong>{tx.organizationName}</strong>
                        </td>
                        <td>{tx.reason}</td>
                        <td className={`num ${tx.delta < 0 ? 'delta--down' : 'delta--up'}`}>
                          {tx.delta > 0 ? '+' : ''}
                          {tx.delta}
                        </td>
                        <td>
                          <Mono>{tx.id.slice(0, 16)}…</Mono>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* ----------------------------------------- Plan Distribution Breakdown */}
          <section className="panel" style={{ marginTop: '24px' }}>
            <SectionTitle>Active Subscription Breakdown</SectionTitle>
            <div className="admin-plans-grid">
              {['free', 'starter', 'standard', 'pro', 'enterprise'].map((p) => {
                const count = data.revenue.planDistribution[p] ?? 0;
                return (
                  <div key={p} className="admin-plan-card">
                    <div className="admin-plan-card__name" style={{ textTransform: 'capitalize' }}>
                      {p} Tier
                    </div>
                    <div className="admin-plan-card__count">{count}</div>
                    <div className="admin-plan-card__sub">active workspaces</div>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
